/**
 * Reconnecting websocket.
 *
 * The socket is read-mostly: it carries broadcasts down and only `resume`,
 * `viewing` and `editing` up. That is what makes reconnection simple — a dropped socket can
 * never lose a write, because writes went over REST.
 */
import type { ClientMsg, ServerEvent } from "@shared/types.js";
import { pulse } from "./netPulse.js";

export interface SocketHandlers {
  onEvent: (event: ServerEvent) => void;
  onStatus: (status: "connecting" | "open" | "offline") => void;
  /** The seq to resume from; read fresh on every reconnect. */
  lastSeq: () => number;
  /** Called after each (re)connect. A new socket is a stranger to the server,
   *  so whatever this tab has open or locked has to be said again. */
  onOpen?: (send: (msg: ClientMsg) => void) => void;
}

export function connect(handlers: SocketHandlers): { send: (msg: ClientMsg) => void; close: () => void } {
  let ws: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let timer: number | undefined;

  const open = (): void => {
    if (closed) return;
    handlers.onStatus("connecting");
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
    ws = new WebSocket(url);

    ws.onopen = () => {
      attempt = 0;
      handlers.onStatus("open");
      ws?.send(JSON.stringify({ t: "resume", since: handlers.lastSeq() } satisfies ClientMsg));
      handlers.onOpen?.((msg) => ws?.send(JSON.stringify(msg)));
    };
    ws.onmessage = (ev) => {
      pulse();
      const event = JSON.parse(String(ev.data)) as ServerEvent;
      // The server sends one frame per tick: a batch is everything one action
      // caused. Applied in order and synchronously, so Vue renders once.
      if (event.t === "batch") for (const e of event.events) handlers.onEvent(e);
      else handlers.onEvent(event);
    };
    ws.onclose = () => {
      ws = null;
      if (closed) return;
      handlers.onStatus("offline");
      // Exponential backoff with a ceiling, so a server restart reconnects
      // quickly but a long outage does not hammer the box.
      const delay = Math.min(15_000, 500 * 2 ** attempt++);
      timer = window.setTimeout(open, delay);
    };
    ws.onerror = () => ws?.close();
  };

  open();

  return {
    send: (msg) => {
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
    },
    close: () => {
      closed = true;
      window.clearTimeout(timer);
      ws?.close();
    },
  };
}
